import type { Locale } from '@mfos/shared';
import { createContext, useContext } from 'react';
import { en, hy, type MessageKey } from './messages';
import { hyPhrases } from './phrases';

export const LOCALE_STORAGE_KEY = 'mfos.locale';

export const LOCALES: readonly Locale[] = ['EN', 'HY', 'RU'];

export function readStoredLocale(): Locale | null {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    return LOCALES.find((locale) => locale === stored) ?? null;
  } catch {
    return null;
  }
}

export function translate(locale: Locale, key: MessageKey): string {
  if (locale === 'HY') return hy[key];
  return en[key];
}

let activeLocale: Locale = 'EN';

/** Keeps phrase() aligned with the provider. Tests never mount the provider, so they stay English. */
export function setActiveLocale(locale: Locale): void {
  activeLocale = locale;
}

/** Translates an English source string when the live locale is Armenian. Unknown text stays as written. */
export function phrase(text: string): string {
  if (activeLocale !== 'HY') return text;
  return hyPhrases[text] ?? text;
}

interface I18nValue {
  readonly locale: Locale;
  readonly setLocale: (locale: Locale) => void;
  readonly t: (key: MessageKey) => string;
}

const fallback: I18nValue = {
  locale: 'EN',
  setLocale: () => undefined,
  t: (key) => en[key],
};

export const I18nContext = createContext<I18nValue | null>(null);

export function useI18n(): I18nValue {
  return useContext(I18nContext) ?? fallback;
}

const ROLE_KEYS = {
  OWNER: 'roleOwner',
  FOUNDER: 'roleFounder',
  EXECUTIVE: 'roleExecutive',
  OBSERVER: 'roleObserver',
} as const;

const KNOWN_ROLES = ['OWNER', 'FOUNDER', 'EXECUTIVE', 'OBSERVER'] as const;
type KnownRole = (typeof KNOWN_ROLES)[number];

function isKnownRole(role: string): role is KnownRole {
  return (KNOWN_ROLES as readonly string[]).includes(role);
}

export function roleLabel(t: (key: MessageKey) => string, role: string): string {
  if (!isKnownRole(role)) return role;
  return t(ROLE_KEYS[role]);
}
